import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OrganizationsAPI, OrgMembersAPI } from '@makyoapp/makyo-sdk'
import { MemberAdapter, OrgCardAdapter } from '@makyoapp/makyo-common'

import { toasted } from '@/lib/toast'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { EVENT_KEYS } from '@/shared/custom-event'

export function useHandleLeaveOrg() {
  const printAPIError = useReqError()
  return useCallback(
    ({
      leaveOrg,
      onBeforeUpdate,
      onAfterUpdate,
    }: {
      leaveOrg: string
      onBeforeUpdate?: () => void
      onAfterUpdate?: () => void
    }) => {
      onBeforeUpdate?.()
      OrgMembersAPI.ReqLeave(leaveOrg)
        .then(() => {
          toasted('Successfully leave org!')
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          onAfterUpdate?.()
        })
    },
    [printAPIError]
  )
}

/**
 * A hook for removing an organization.
 *
 * @param org - An optional object representing the organization to remove.
 * @param onHide - An optional function to call when the remove modal is hidden.
 * The function takes two boolean arguments: isSubmit and isTransfer. isSubmit is
 * true if the removal was submitted false if removal were cancelled,
 * and isTransfer is true if the removal involved transferring ownership of the organization.
 *
 * @returns An object with four properties:
 * - rmTransferTarget: The current transfer target (a member object) for the removal.
 * - setRmTransferTarget: A function for setting the transfer target for the removal.
 * - handleRm: A callback function for removing the organization.
 * - handleRmHide: A callback function for hiding the remove modal.
 */
export function useOrgRemove(org?: OrgCardAdapter, onHide?: (isSubmit: boolean, isTransfer: boolean) => void) {
  const { t } = useTranslation()
  const printAPIError = useReqError()
  const { userData } = useAuth()
  const userId = userData?.id

  const {
    handleTransfer,
    transferTarget: rmTransferTarget,
    setTransferTarget: setRmTransferTarget,
    handleHideTransfer,
  } = useOrgTransferOwnership(org)

  const handleRmHide = useCallback(
    (isSubmit: boolean) => {
      const isTransfer = rmTransferTarget !== undefined
      handleHideTransfer()
      onHide?.(isSubmit, isTransfer)
    },
    [handleHideTransfer, onHide, rmTransferTarget]
  )

  const handleRm = useCallback(() => {
    if (org) {
      if (!rmTransferTarget) {
        return OrganizationsAPI.ReqDelete(org.id)
          .then(() => {
            toasted(t('Organization {{name}} deleted!', { name: org.name }))
            // cleanup local recent apps for deleted org
            try {
              if (userId) {
                const key = `recentApps:${userId}`
                const raw = localStorage.getItem(key)
                const parsed = raw ? JSON.parse(raw) : []
                const list: any[] = Array.isArray(parsed) ? parsed : []
                const filtered = list.filter((a) => a && a.org?.id !== org.id)
                localStorage.setItem(key, JSON.stringify(filtered))
                window.dispatchEvent(new CustomEvent(EVENT_KEYS.RECENT_APPS_UPDATED, { detail: filtered }))
              }
            } catch (_) {
              // ignore localStorage errors
            }
          })
          .catch(printAPIError)
      } else {
        return handleTransfer()
      }
    } else {
      return Promise.reject({
        message: 'Invalid parameter',
      })
    }
  }, [org, rmTransferTarget, handleTransfer, t, printAPIError])

  return {
    rmTransferTarget,
    setRmTransferTarget,
    handleRm,
    handleRmHide,
  }
}

/**
 * A hook for transferring ownership of an organization.
 *
 * @param transferOrg - An optional object representing the organization to transfer.
 * @param onHide - An optional function to call when the transfer modal is hidden.
 * @returns An object with four properties:
 * - transferTarget: The current transfer target (a member object).
 * - setTransferTarget: A function for setting the transfer target.
 * - handleTransfer: A callback function for transferring ownership of the organization.
 * - handleHideTransfer: A callback function for hiding the transfer modal.
 */
export function useOrgTransferOwnership(transferOrg?: OrgCardAdapter, onHide?: () => void) {
  const { t } = useTranslation()
  const [transferTarget, setTransferTarget] = useState<MemberAdapter | undefined>(undefined)

  const printAPIError = useReqError()

  const handleTransfer = useCallback(() => {
    if (transferOrg && transferTarget) {
      return OrganizationsAPI.ReqTransferOwnership(transferOrg.id, transferTarget.id)
        .then(() => {
          toasted(
            t('Organization transfer request for {{org}} to {{name}} have been sent', {
              org: transferOrg.name,
              name: transferTarget.name || transferTarget.email,
            })
          )
        })
        .catch(printAPIError)
    } else {
      return Promise.reject({
        message: 'Invalid parameter',
      })
    }
  }, [transferOrg, transferTarget, t, printAPIError])

  const handleHideTransfer = useCallback(() => {
    onHide?.()
    setTransferTarget(undefined)
  }, [onHide])

  return {
    transferTarget,
    setTransferTarget,
    handleTransfer,
    handleHideTransfer,
  }
}
